import React, { useState, useEffect, useCallback } from 'react';
import {
  ViewMode,
  Accident,
  UserSettings,
  RadiusConfig,
  PostcodeConfig,
  PolygonConfig,
  ReplacementVehicleRequest,
  WazeDataState,
} from './types';
import { fetchIncidents } from './services/incidentsService';
import { filterAccidentsByZones, getZonesBoundingBox } from './services/zoneService';
import { useAuth } from './contexts/AuthContext';
import { useProfile } from './hooks/useProfile';
import { useZones } from './hooks/useZones';
import { usePushNotifications } from './hooks/usePushNotifications';
import { initServiceWorker } from './services/pushService';
import { OnboardingFlow } from './components/onboarding/OnboardingFlow';
import { MainLayout } from './components/layout/MainLayout';
import { AppContent } from './components/AppContent';
import { SignInPage } from './components/SignInPage';
import { Loader } from './components/Loader';
import { IncidentToast } from './components/IncidentToast';
import { logger } from './lib/logger';
import './App.css';

const log = logger.createNamespace('App');

const SYDNEY_BOUNDS = {
  north: -33.5,
  south: -34.2,
  east: 151.5,
  west: 150.5,
};

const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const App: React.FC = () => {
  const { user, loading: authLoading, signOut: authSignOut } = useAuth();
  const { profile, loading: profileLoading, updateProfile } = useProfile();
  const { zones: dbZones, addZone, deleteZone, toggleZone } = useZones();

  const [onboardingStep, setOnboardingStep] = useState<
    'welcome' | 'zone-setup' | 'permissions' | 'complete'
  >('welcome');

  const [currentView, setCurrentView] = useState<ViewMode['current']>('welcome');
  const [selectedAccident, setSelectedAccident] = useState<Accident | null>(null);

  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  const [sortedAccidents, setSortedAccidents] = useState<Accident[]>([]);

  const [replacementRequests, setReplacementRequests] =
    useState<ReplacementVehicleRequest[]>([]);
  const [currentReplacementAccident, setCurrentReplacementAccident] = useState<Accident | null>(null);
  const [currentLeadName, setCurrentLeadName] = useState<string>('');
  const [currentLeadInfo, setCurrentLeadInfo] = useState<{
    name: string;
    email: string;
    phone: string;
  } | null>(null);
  const [currentTrackingRequest, setCurrentTrackingRequest] =
    useState<ReplacementVehicleRequest | null>(null);

  const [incidentAccidents, setIncidentAccidents] = useState<Accident[]>([]);
  const [wazeDataState, setWazeDataState] = useState<WazeDataState>({
    isLoading: false,
    lastUpdated: null,
    error: null,
    isLive: false,
    incidentCount: 0,
  });
  const [incidentToast, setIncidentToast] = useState<{
    title: string;
    body?: string;
  } | null>(null);

  const [userSettings, setUserSettings] = useState<UserSettings>({
    notificationRadius: 5,
    subscribedAreas: [],
    pushNotifications: true,
    emailNotifications: false,
    zones: [],
    hasCompletedWelcome: false,
    dataSources: {
      waze: true,
      transportNsw: true,
      psn: true,
    },
    blockedUsers: [],
  });

  const fetchIncidentData = useCallback(
    async (showLoadingState = true, bypassCache = false) => {
      if (showLoadingState) {
        setWazeDataState(prev => ({ ...prev, isLoading: true, error: null }));
      }

      try {
        const bounds = getZonesBoundingBox(dbZones) || SYDNEY_BOUNDS;
        const result = await fetchIncidents(
          bounds.north,
          bounds.south,
          bounds.east,
          bounds.west,
          bypassCache
        );

        if (result.error) {
          setWazeDataState(prev => ({
            ...prev,
            isLoading: false,
            error: result.error || 'Unknown error',
            isLive: false,
          }));
        } else {
          setIncidentAccidents(result.accidents);
          setWazeDataState({
            isLoading: false,
            lastUpdated: result.fetchedAt,
            error: null,
            isLive: true,
            incidentCount: result.accidents.length,
          });
        }
      } catch (error) {
        log.error('Failed to fetch incident data', error);
        setWazeDataState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to fetch live data',
          isLive: false,
        }));
      }
    },
    [dbZones]
  );

  const handleRefreshData = useCallback(() => {
    fetchIncidentData(true, true);
  }, [fetchIncidentData]);

  const handleIncidentPush = useCallback(() => {
    fetchIncidentData(true, true);
  }, [fetchIncidentData]);

  usePushNotifications({
    onIncidentPush: handleIncidentPush,
    onShowToast: (toast) => setIncidentToast({ title: toast.title, body: toast.body }),
    onboardingComplete: onboardingStep === 'complete',
  });

  useEffect(() => {
    initServiceWorker();
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        error => {
          log.debug('Geolocation unavailable', { code: error.code });
          setUserLocation(null);
        }
      );
    } else {
      setUserLocation(null);
    }
  }, []);

  useEffect(() => {
    if (user && profile && !profileLoading) {
      log.debug('Checking onboarding status', { completed: profile.onboarding_completed });

      if (profile.onboarding_completed) {
        setOnboardingStep('complete');
        setCurrentView('list');
      }
    }
  }, [user, profile, profileLoading]);

  useEffect(() => {
    if (user && onboardingStep === 'complete') {
      fetchIncidentData(true);
    }
  }, [user, onboardingStep, fetchIncidentData]);

  useEffect(() => {
    const allAccidents: Accident[] = incidentAccidents;
    const filteredByZones = filterAccidentsByZones(allAccidents, dbZones);

    const accidentsWithDistance = filteredByZones.map(accident => ({
      ...accident,
      distance: userLocation
        ? calculateDistance(
            userLocation.lat,
            userLocation.lng,
            accident.location.lat,
            accident.location.lng
          )
        : undefined,
    }));

    const sorted = accidentsWithDistance.sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );

    setSortedAccidents(sorted);
  }, [userLocation, incidentAccidents, dbZones]);

  const handleViewOnMap = (accident: Accident) => {
    setSelectedAccident(accident);
    setCurrentView('map');
  };

  const handleZoneClick = () => {
    setCurrentView('zones');
  };

  const handleBottomNavigation = (view: string) => {
    if (view === 'home') {
      setCurrentView('list');
    } else if (view === 'requests') {
      setCurrentView('requests');
    } else {
      setCurrentView(view as ViewMode['current']);
    }
  };

  const handleToggleZone = async (zoneId: string) => {
    await toggleZone(zoneId);
  };

  const handleDeleteZone = async (zoneId: string) => {
    await deleteZone(zoneId);
  };

  const handleAddZone = async (
    name: string,
    type: 'radius' | 'postcodes' | 'polygon',
    config: RadiusConfig | PostcodeConfig | PolygonConfig
  ) => {
    const result = await addZone(name, type, config);

    if (!result.error) {
      setUserSettings(prev => ({
        ...prev,
        hasCompletedWelcome: true,
      }));
      setCurrentView('list');
    }
  };

  const handleReplacementRequest = (accident: Accident) => {
    setCurrentReplacementAccident(accident);
    setCurrentView('replacement-request');
  };

  const handleReplacementRequestSubmit = (formData: {
    name: string;
    email: string;
    phone: string;
  }) => {
    setCurrentLeadName(formData.name);
    setCurrentLeadInfo(formData);
    setCurrentView('replacement-loading');
  };

  const handleReplacementComplete = () => {
    if (currentReplacementAccident && currentLeadInfo) {
      const newRequest: ReplacementVehicleRequest = {
        id: `req-${Date.now()}`,
        accidentId: currentReplacementAccident.id,
        leadInfo: {
          fullName: currentLeadInfo.name,
          email: currentLeadInfo.email,
          phone: currentLeadInfo.phone,
        },
        accident: currentReplacementAccident,
        status: 'dispatched',
        dispatchLocation: {
          lat: -33.9288,
          lng: 151.1956,
          address: "4/221-233 O'Riordan St, Mascot NSW 2020",
        },
        estimatedArrival: new Date(Date.now() + 20 * 60 * 1000),
        dispatchedAt: new Date(),
        requestedAt: new Date(),
      };

      setReplacementRequests(prev => [newRequest, ...prev]);
    }

    setCurrentView('requests');
    setCurrentReplacementAccident(null);
    setCurrentLeadName('');
    setCurrentLeadInfo(null);
    setCurrentTrackingRequest(null);
  };

  const handleViewTracking = (request: ReplacementVehicleRequest) => {
    setCurrentTrackingRequest(request);
    setCurrentView('replacement-tracking');
  };

  const handleSignIn = () => {
    setOnboardingStep('welcome');
    setCurrentView('welcome');
  };

  const handleSignOut = async () => {
    await authSignOut();
    setOnboardingStep('welcome');
    setCurrentView('welcome');
    setUserSettings({
      notificationRadius: 5,
      subscribedAreas: [],
      pushNotifications: true,
      emailNotifications: false,
      zones: [],
      hasCompletedWelcome: false,
      dataSources: {
        waze: true,
        transportNsw: true,
        psn: true,
      },
      blockedUsers: [],
    });
  };

  const handleWelcomeComplete = () => {
    setOnboardingStep('zone-setup');
    setCurrentView('add-zone');
  };

  const handleWelcomeSkip = async () => {
    log.info('Skipping onboarding');
    const { error } = await updateProfile({ onboarding_completed: true });
    if (error) {
      log.error('Failed to save onboarding status', error);
      alert('Failed to save: ' + error.message);
    }
    setOnboardingStep('complete');
    setUserSettings(prev => ({ ...prev, hasCompletedWelcome: true }));
    setCurrentView('list');
  };

  const handleZoneSetupComplete = async (
    name: string,
    type: 'radius' | 'postcodes' | 'polygon',
    config: RadiusConfig | PostcodeConfig | PolygonConfig
  ) => {
    const result = await addZone(name, type, config);
    if (result.error) {
      log.error('Failed to save zone during onboarding', result.error);
      alert('Failed to save zone: ' + result.error.message);
      return;
    }
    setOnboardingStep('permissions');
    setCurrentView('permissions');
  };

  const handlePermissionsComplete = async () => {
    log.info('Completing onboarding');
    const { error } = await updateProfile({ onboarding_completed: true });
    if (error) {
      log.error('Failed to save onboarding status', error);
      alert('Failed to save onboarding status: ' + error.message);
    }
    setOnboardingStep('complete');
    setUserSettings(prev => ({ ...prev, hasCompletedWelcome: true }));
    setCurrentView('list');
  };

  const handlePermissionsSkip = async () => {
    log.info('Skipping permissions, completing onboarding');
    const { error } = await updateProfile({ onboarding_completed: true });
    if (error) {
      log.error('Failed to save onboarding status', error);
      alert('Failed to save onboarding status: ' + error.message);
    }
    setOnboardingStep('complete');
    setUserSettings(prev => ({ ...prev, hasCompletedWelcome: true }));
    setCurrentView('list');
  };

  const enabledZones = dbZones.filter(z => z.enabled);
  const currentZoneText =
    enabledZones.length === 0
      ? 'No Zone'
      : enabledZones.length === 1
        ? enabledZones[0].name
        : `${enabledZones.length} selected`;

  const getActiveTab = () => {
    if (currentView === 'list' || currentView === 'map') return 'home';
    if (currentView === 'zones' || currentView === 'add-zone') return 'zones';
    if (
      currentView === 'requests' ||
      currentView === 'replacement-request' ||
      currentView === 'replacement-loading' ||
      currentView === 'replacement-tracking'
    )
      return 'requests';
    if (currentView === 'settings') return 'settings';
    return 'home';
  };

  const isAdminRoute =
    window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/');

  if (authLoading) {
    return (
      <div
        className="app-container"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Loader size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app-container">
        <SignInPage onSignIn={handleSignIn} />
      </div>
    );
  }

  if (
    onboardingStep !== 'complete' &&
    (currentView === 'welcome' || currentView === 'add-zone' || currentView === 'permissions')
  ) {
    return (
      <div className="app-container">
        <OnboardingFlow
          step={onboardingStep}
          onWelcomeComplete={handleWelcomeComplete}
          onWelcomeSkip={handleWelcomeSkip}
          onZoneSetupComplete={handleZoneSetupComplete}
          onPermissionsComplete={handlePermissionsComplete}
          onPermissionsSkip={handlePermissionsSkip}
          onBackToWelcome={() => setOnboardingStep('welcome')}
        />
      </div>
    );
  }

  return (
    <>
      {incidentToast && (
        <IncidentToast
          title={incidentToast.title}
          body={incidentToast.body}
          onDismiss={() => setIncidentToast(null)}
        />
      )}
      <MainLayout
        currentView={currentView}
        onViewChange={setCurrentView}
        currentZoneText={currentZoneText}
        onZoneClick={handleZoneClick}
        hideZoneInfo={currentView === 'add-zone' && dbZones.length === 0}
        activeTab={getActiveTab()}
        onTabChange={handleBottomNavigation}
      >
        <AppContent
          currentView={currentView}
          user={user}
          profile={profile}
          authLoading={authLoading}
          profileLoading={profileLoading}
          onSignIn={handleSignIn}
          onSignOut={handleSignOut}
          isAdminRoute={isAdminRoute}
          sortedAccidents={sortedAccidents}
          selectedAccident={selectedAccident}
          dbZones={dbZones}
          userSettings={userSettings}
          wazeDataState={wazeDataState}
          replacementRequests={replacementRequests}
          currentReplacementAccident={currentReplacementAccident}
          currentLeadName={currentLeadName}
          currentTrackingRequest={currentTrackingRequest}
          setCurrentView={setCurrentView}
          onViewOnMap={handleViewOnMap}
          onReplacementRequest={handleReplacementRequest}
          onSettingsChange={setUserSettings}
          onRefresh={handleRefreshData}
          onToggleZone={handleToggleZone}
          onDeleteZone={handleDeleteZone}
          onAddZone={handleAddZone}
          onReplacementSubmit={handleReplacementRequestSubmit}
          onReplacementComplete={handleReplacementComplete}
          onViewTracking={handleViewTracking}
        />
      </MainLayout>
    </>
  );
};

export default App;
